<template>
	<component :is="getComponent(definition)" :part="partDefinition" :data="model" @change="$emit('changed')" />
	<span class="perr" v-if="errorMessage">{{errorMessage}}</span>
</template>

<script setup lang="ts">
	import {ref, computed, inject} from "vue";
	import type {MergeDefinition} from "@t/agenda/Consolidator";
	import InputPart from '@/fd/editors/InputPart.vue';
	import SelectPart from '@/fd/editors/SelectPart.vue';
	import TextAreaPart from '@/fd/editors/TextAreaPart.vue';
	import CatsPart from '@/fd/editors/CatsPart.vue';
	import TemporalPart from '@/fd/editors/temporal/TemporalPart.vue';
	import EaddressPart from '@/fd/editors/eaddress/EAddressPart.vue';
	import ArrayPart from '@/fd/containers/ArrayPart.vue';
	import SuggesterPart from '@/fd/editors/suggester/SuggesterPart.vue';
	import AvatarPart from '@/fd/editors/file/AvatarPart.vue';
	import CheckboxPart from '@/fd/editors/CheckboxPart.vue';
	import HtmlAreaPart from '@/fd/editors/html/HtmlAreaPart.vue';

	const SUGG_TYPES = ['N', 'J', 'O', 'K', 'G', 'C', '|'];
	
	const props = defineProps<{
		data: any,
		conflict: boolean
	}>();
	
	const definition = inject<MergeDefinition>('definition');
	const errorMessage = ref<string|null>(null);
	
	const model = defineModel();
	
	const emit = defineEmits(['changed']);
	
	const inputClass = computed<string>(() => {
		return props.conflict?"error":"";
	});
	
	const isSuggesterType = computed(() => {
		return SUGG_TYPES.includes(definition.type);
	});
	
	const partDefinition = computed(() => {
		var part = {
			property: definition.property,
			catch: definition.property,
			options: { clazz: inputClass.value }
		};
		if (definition.select) {
			part.type="select";
			part.list=definition.listName;
			part.emptyItem= {value: undefined, name: ""};
		}
		if (isSuggesterType.value) {
			part.url="suggestfeed.do";
			part.minTags=0;
			part.maxTags=1;
			part.tag=[
				{d: definition.property, t: "id", empty: 0 },
				{d: definition.property+"Name", t: "name", empty: "",
					loadIfMissing: { url: "reverseSuggest.do", params: [{ name: "id", d: definition.property, empty: "-" }]}}
			];
			part.propagate=[
				{ d: definition.property, s: "id.main", c: "integer", empty: 0},
				{ d: definition.property+"Name", s: "title.main", empty: "" }
			];
		}
		
		switch(definition.type) {
			case "^":
			case "S":
				if (definition.select) {
					part.cast="string";
				}
				else {
					part.type="input";
					part.itype="text";
					part.maxLength=255;
					part.size=24;
				}
				break;
			case "€":
					part.type="input";
					part.itype="url";
					part.maxLength=255;
					part.size=24;
					break;
			case "I": 
			case "V": 
			case "F": 
				if (definition.select) {
					part.cast="integer";
				}
				else {
					part.type="input";
					part.itype="number";
					part.cast="integer";
					part.maxLength=16;
					part.size=8;
				}
				break;
			case "_":
					part.type="cats";
					part.cats="USERS_GROUPS";
					part.list=null;
					break;
			case "U":
					part.type="cats";
					part.cats="USERS";
					part.max=1;
					part.list=null;
					break;
			case "T": 
			case "M": 
					part.type="cats";
					part.list=definition.listName;
					break;
			case "Q": 
					part.type="textarea";
					part.rows=4;
					part.autosize=true;
					break;
			case "R": 
					part.type="htmlarea";
					break;
			case "*":  
					part.type="temporal";
					part.timePolicy=1;
					break;
			case "!":
					part.type="temporal";
					part.timePolicy=2;
					break;
			case "D":  
					part.type="temporal";
					part.timePolicy=0;
					break;
			case "C":  
					part.type="suggest";
					part.suggester="CustomerSuggester"
					part.srcType=7;
					break;
			case "J":  
					part.type="suggest";
					part.suggester="CampaignSuggester"
					part.srcType=26;
					break;
			case "O":  
					part.type="suggest";
					part.suggester="OpportunitySuggester"
					part.srcType=4;
					break;
			case "K":  
					part.type="suggest";
					part.suggester="BusCaseSuggester"
					part.srcType=5;
					break;
			case "G":  
					part.type="suggest";
					part.suggester="ProblemSuggester"
					part.srcType=9;
					break;
			case "N":  
					part.type="suggest";
					part.suggester="RegionSuggester";	
					part.srcType=36;
					break;
			case "|":  
					part.type="suggest";
					part.suggester="FolderSuggester";	
					part.srcType=24;
					break;
			case "@":
					part = {
                        type: "array",
                        property: definition.property,
                        sortable: true,
                        item: { type: "eaddress" },
                        add: [{
							label: "přidat kontakt",
							init: "value",
							value: { id:0, type: 1, junk: false, priority: 100, value: "", raw: "", tags:[] }
						}]
                    };
					break;
			case "§":
				part.type="avatar";
				break;
			case "B":
				part.type="checkbox";
				break;
			default:
				errorMessage.value = "Undefined component for:"+definition.type;
				return null;
		}
		return part;
	});
	
	function getComponent(definition: MergeDefinition) {
		//console.log("def:"+definition.property+" data:"+props.data);
		//console.log("conflict:"+props.conflict+" listName:"+definition.listName);	
		switch(definition.type) {
			case "^":
			case "S":
			case "I": 
			case "V": 
			case "F": return definition.select?SelectPart:InputPart;
			case "€": return InputPart;
			case "T": 
			case "_":
			case "U":
			case "M": return CatsPart;
			case "Q": return TextAreaPart;
			case "*":  
			case "!": 
			case "D":  return TemporalPart;
			case "§": return AvatarPart;
			case "@": return ArrayPart;
			case "|": 
			case "N": 
			case "J":	
			case "O": 
			case "K": 
			case "G": 
			case "C": return SuggesterPart;
			case "B": return CheckboxPart;
			case "R": return HtmlAreaPart;
			default:
				errorMessage.value = "Undefined component for:"+definition.type;
				return null;
		}
	}
	
</script>
